<template>
  <ion-page>
    <ion-header :translucent="true">
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-menu-button color="primary" />
        </ion-buttons>
        <ion-title v-tl:HOME_TITLE size="medium" />
        <ion-buttons slot="end">
          <ion-button @click="showPopover">
            <ion-icon :icon="ellipsisVertical" />
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content :fullscreen="true">
      <ion-list>

        <list-goal v-for="item in pendingGoals" :key="item.key" :goal-key="item.key" />
        <list-goal v-for="item in currentGoals" :key="item.key" :goal-key="item.key" />

        <template v-if="overdueGoals.length > 0">
          <ion-item-group>
            <ion-item-divider>
              <ion-label v-tl:HOME_GOALS_SECTION_OVERDUE />
            </ion-item-divider>
            <list-goal v-for="item in overdueGoals" :key="item.key" :goal-key="item.key" />
          </ion-item-group>
        </template>

        <template v-if="completedGoals.length > 0">
          <ion-item-group>
            <ion-item-divider>
              <ion-label v-tl:HOME_GOALS_SECTION_COMPLETE />
            </ion-item-divider>
            <list-goal v-for="item in completedGoals" :key="item.key" :goal-key="item.key" />
          </ion-item-group>
        </template>
        
        <template v-if="archivedGoals.length > 0">
          <ion-item-group>
            <ion-item-divider>
              <ion-label v-tl:HOME_GOALS_SECTION_ARCHIVED />
            </ion-item-divider>
            <list-goal v-for="item in archivedGoals" :key="item.key" :goal-key="item.key" />
          </ion-item-group>
        </template>
      </ion-list>
      
      <!-- bump the list up one so the FAB doesn't obscure anything -->
      <ion-item disabled />
      <ion-fab slot="fixed" vertical="bottom" horizontal="end">
        <ion-fab-button @click="$router.push('/edit')">
          <ion-icon :icon="add" />
        </ion-fab-button>
      </ion-fab>
    </ion-content>
  </ion-page>
</template>

<script>
import {
    IonContent,
    IonHeader,
    IonButton,
    IonButtons,
    IonPage,
    IonIcon,
    IonTitle,
    IonToolbar,
    IonItem,
    IonLabel,
    IonItemDivider,
    IonItemGroup,
    IonList,
    IonFab,
    IonFabButton,
    IonMenuButton,
    popoverController,
} from '@ionic/vue';
import { defineComponent } from 'vue';
import { add, ellipsisHorizontal, ellipsisVertical } from 'ionicons/icons';

import ListGoal from '@/views/ListGoal.vue';
import Popover from '@/views/OptionsPopover.vue';
import { filter, map } from 'lodash';
import { asyncObject } from '@/async';
import { isComplete } from '@/goal';

export default defineComponent({
    name: 'Home',
    components: {
        IonContent,
        IonHeader,
        IonPage,
        IonButton,
        IonButtons,
        IonIcon,
        IonItem,
        IonItemDivider,
        IonItemGroup,
        IonList,
        IonLabel,
        IonTitle,
        IonToolbar,
        ListGoal,
        IonFab,
        IonFabButton,
        IonMenuButton,
    },

    setup() {
        return {
            add,
            ellipsisHorizontal,
            ellipsisVertical,
        };
    },

    data() {
        return {
            reorder: false,
        };
    },

    computed: {
        allGoals() {
            const { $store } = this;
            return $store.getters.activeGoalKeys.map(key => ({
                key,
                async: asyncObject.wrap($store.dispatch('goals/loadGoal', key)),
            }));
        },
        pendingGoals() {
            return filter(this.allGoals, x => !x.async.ready);
        },
        activeGoals() {
            const ready = filter(this.allGoals, x => x.async.ready);
            const mapped = map(ready, x => x.async.value);
            return mapped;
        },
        currentGoals() {
            return filter(this.activeGoals, x =>
                !x.archived &&
				!isComplete(x, x.historyCollection) &&
				!x.isOverdue(this.$today, x.historyCollection)
            );
        },

        overdueGoals() {
            return filter(this.activeGoals, x =>
                !isComplete(x, x.historyCollection) &&
				x.isOverdue(this.$today, x.historyCollection)
            );
        },

        completedGoals() {
            if (!this.$store.state.options.showCompleted) return [];
            return filter(this.activeGoals, x => isComplete(x, x.historyCollection));
        },

        archivedGoals() {
            const { $store } = this;
            if (!$store.state.options.showArchived) return [];
            return this.$store.getters.archivedGoalKeys.map(key => ({
                key,
                async: $store.dispatch('goals/loadGoal', key),
            }));
        },
    },

    methods: {
        async showPopover(ev) {
            const popover = await popoverController.create({
                component: Popover,
                cssClass: 'adaptive-popover',
                event: ev,
                translucent: true,
                size: 'auto',
            });
            await popover.present();
            await popover.onDidDismiss();
        },
    },
});

</script>

<style scoped>
#container {
    text-align: center;

    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
}

#container strong {
    font-size: 20px;
    line-height: 26px;
}

#container p {
    font-size: 16px;
    line-height: 22px;

    color: #8c8c8c;

    margin: 0;
}

#container a {
    text-decoration: none;
}
</style>